import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocale } from "../context/LocaleContext";
import api from "../services/api";

interface AboutPageData {
  title: string;
  subtitle: string;
  description: string;
  storyTitle: string;
  storyContent: string;
  contactTitle: string;
  contactDescription: string;
  contactButtonText: string;
  contactButtonUrl: string;
}

export function AboutPage() {
  const { locale } = useLocale();
  const [aboutData, setAboutData] = useState<AboutPageData>({
    title: "About",
    subtitle: "Cagla",
    description:
      "DevOps engineer passionate about cloud infrastructure, automation, and building scalable systems. I share what I learn through practical tutorials.",
    storyTitle: "My Story",
    storyContent:
      "<p>I'm a DevOps engineer who loves solving infrastructure challenges and automating workflows.</p><p>This blog is where I document my journey, share solutions to problems I've encountered, and explore new tools in the DevOps ecosystem.</p>",
    contactTitle: "Get In Touch",
    contactDescription:
      "Let's connect! I'm always open to discussing DevOps, cloud technologies, or potential collaborations.",
    contactButtonText: "Visit Portfolio",
    contactButtonUrl: "https://github.com/yourusername",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await api.getAboutPage(locale);
        //console.log("About page response:", response);

        // Check if response has data
        if (response?.data?.attributes) {
          setAboutData(response.data.attributes);
        } else if (response?.data) {
          // Handle case where data is directly in response.data
          setAboutData(response.data);
        }
        // If no data, keep using the default fallback data
      } catch (error: any) {
        console.error("Failed to fetch about page data:", error);
        // Keep using fallback data on error
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, [locale]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-600 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative pt-36 pb-24 px-4 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-black to-black" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-600/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {aboutData.title}{" "}
              <span className="text-red-600">{aboutData.subtitle}</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              {aboutData.description}
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/*       <section className="py-20 px-4 bg-gray-900">
        <div className="container mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-center mb-12"
          >
            What We <span className="text-red-600">Stand For</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-black border border-red-900/20 rounded-xl p-8 hover:border-red-600/50 transition-all"
              >
                <value.icon className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="text-2xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-400 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      <section className="py-20 px-4 bg-black">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {aboutData.storyTitle.split(" ")[0]}{" "}
              <span className="text-red-600">
                {aboutData.storyTitle.split(" ").slice(1).join(" ")}
              </span>
            </h2>
            <div
              className="space-y-6 text-gray-400 leading-relaxed text-lg"
              dangerouslySetInnerHTML={{ __html: aboutData.storyContent }}
            />
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {aboutData.contactTitle.split(" ")[0]}{" "}
              <span className="text-red-600">
                {aboutData.contactTitle.split(" ").slice(1).join(" ")}
              </span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              {aboutData.contactDescription}
            </p>
            <motion.a
              href={aboutData.contactButtonUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              {aboutData.contactButtonText}
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
